import './TourForm.css';
import React from 'react';
import GlobalProps from '../../../../components/globalProps';
import { TourEdit, addTour, patchTour } from '../../../../model/tour';
import { ModalController } from '../../../../components/Modal/ControlledModal';
import { TextArea } from '../../../../components/Form/TextArea/TextArea';
import { TextField } from '../../../../components/Form/TextField/TextField';
import { FileUpload } from '../../../../components/Form/FileUpload/FileUpload';
import { Checkbox } from '../../../../components/Form/Checkbox/Checkbox';
import { Form, useChanges } from '../../../../components/Form/Form';
import { handleError } from '../../../../model/notification';
import { history } from '../../../../model/history';
import { tourPath } from '../../../../routes';
import { AsyncSearch } from '../../../../components/Form/AsyncSearch/AsyncSearch';
import { t } from '../../../../locales/translate';
import { Label } from '../../../../components/Form/Label/Label';
import { Switcher } from '../../../../components/Form/Switcher/Switcher';
import { Stack } from '../../../../components/Stack/Stack';
import { Spacer } from '../../../../components/Spacer/Spacer';
import { ArtistSelect } from '../../../../components/artist_select/artist_select';

interface Props extends GlobalProps {
  controller: ModalController<TourEdit>;
}

export function TourForm({ controller }: Props): JSX.Element {
  const [changes, setChanges, tour, trackChanges] = useChanges<TourEdit>({
    ...controller.model,
    artistIds: (controller.model.artists && controller.model.artists.map((e) => e.id)) || [],
  });

  async function handleOnSubmit(): Promise<void> {
    const changesToSubmit = { ...changes };
    if (changesToSubmit.ticketImage !== null) {
      changesToSubmit.ticketImage = changes.ticketImage && changes.ticketImage.split(',')[1];
    }
    try {
      if (tour.id) {
        await patchTour(tour.id, changesToSubmit);
        controller.close();
      } else {
        const newTour = await addTour(changesToSubmit);
        history.push(tourPath(newTour));
      }
    } catch (error) {
      handleError(error);
    }
  }

  async function handleImageChange(files: File[]): Promise<void> {
    if (files.length <= 0) {
      return setChanges({
        ...changes,
        ticketImage: undefined,
      });
    }
    const reader = new FileReader();

    reader.readAsDataURL(files[0]);

    reader.onload = () => {
      if (reader.result && typeof reader.result === 'string') {
        return setChanges({
          ...changes,
          ticketImage: reader.result,
        });
      }
    };
  }

  return (
    <div className="add-tour">
      <Form
        model={tour}
        onInput={(m, key) => {
          if (key === 'shopId') {
            setChanges({ ...m, artistIds: [] });
          } else {
            trackChanges(m, key);
          }
        }}
      >
        {(form) => {
          controller.configure({
            onSubmit: handleOnSubmit,
            canSubmit: form.valid,
          });
          return (
            <Stack>
              <TextField name="internalName" label={t.model.tour.internalTitle} />
              <TextField
                name="name"
                label={t.model.tour.displayTitle}
                description={t.model.tour.displayTitleMessage}
                required
              />
              <TextField name="subtitle" label={t.general.subtitle} />
              <AsyncSearch
                name="shopId"
                label={t.model.shop._name}
                searchIn={['shop']}
                required
                disabled={!!tour.id}
                description={tour.id ? `* ${t.general.cantBeEdited}` : ''}
              />
              <Spacer size="none" />
              <Checkbox name="subtitleInShop" label={t.model.tour.showSubtitle} />
              <TextArea name="description" label={t.general.description} />
              <Switcher
                label={t.model.category.mainCategory}
                value={tour.mainCategory}
                onChange={(v) => setChanges({ ...changes, ...{ mainCategory: v } })}
                labels={[t.general.yes, t.general.no]}
              />
              <FileUpload label={t.model.ticket.ticketImage} onChange={handleImageChange} image={tour.ticketImage} />
              <ArtistSelect
                shopId={tour.shopId}
                loadingMsg={t.actions.loading(t.model.tourArtists._plural)}
                label={t.model.tourArtists._name}
                name="artistIds"
              />
              <Switcher
                value={tour.excludeFromFeeds}
                label={t.model.product.excludeFromFeeds}
                labels={[t.general.yes, t.general.no]}
                onChange={(value) => setChanges({ ...changes, excludeFromFeeds: value })}
              />
              <Spacer size="none" />
              <Label title={t.model.tour.sortOrder}></Label>
              <TextField name="order" label={t.model.tour.sortOrder} />
              <TextField name="sortOrderHomepage" label={t.model.tour.sortOrderHomepage} />
              <TextField name="sortOrderTicketTopRight" label={t.model.tour.sortOrderTicketTopRight} />
              <TextField name="sortOrderTicketTopLeft" label={t.model.tour.sortOrderTicketTopLeft} />
              <TextField name="sortOrderTicketBottom" label={t.model.tour.sortOrderTicketBottom} />
              <Spacer size="none" />
            </Stack>
          );
        }}
      </Form>
    </div>
  );
}
